import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { toast } from "sonner";
import { missingPrerequisites, obsoletePermissionKeys, savablePermissionKeys } from "../lib/roles";
import { useBottomBar } from "../hooks/useCopilotDock";
import BottomActionBar from "../components/BottomActionBar";
import { Loader2, Plus, Search, Crown, Shield, Users as UsersIcon, User, CheckSquare, Settings, LayoutGrid, ChevronRight, ChevronDown, ChevronUp, Trash2, UsersRound, Headset, GraduationCap, BookOpen, Lock, AlertTriangle, MapPinned, Sparkles, Printer, Store, Ticket, Receipt, FileText, Lightbulb, Package, Megaphone, Landmark, Gauge, ShieldCheck, Workflow, Network } from "lucide-react";

const CATEGORY_META = {
  prospects: { label: "Prospects", description: "Gestion des prospects et du pipeline", icon: User },
  customers: { label: "Clients", description: "Dossiers des pharmacies clientes après leur conversion : mise en service, statut, responsable du compte", icon: Store },
  tasks: { label: "Tâches", description: "Gestion des tâches et suivi des actions", icon: CheckSquare },
  collaborators: { label: "Collaborateurs", description: "Annuaire des personnes qui travaillent avec Jamora, services et zones", icon: UsersRound },
  skills: { label: "Compétences et habilitations", description: "Fonctions métier, compétences, niveaux et habilitations des collaborateurs (aucune fonction ne donne d'accès)", icon: GraduationCap },
  missions: { label: "Missions terrain", description: "Interventions dans les pharmacies : types, planning, équipe et déroulement", icon: MapPinned },
  equipment: { label: "Matériel des pharmacies", description: "Parc matériel, contrôles et anomalies. L'équipe d'une mission voit déjà le matériel de sa pharmacie", icon: Printer },
  finance: { label: "Finances internes", description: "Dépenses, contributions et justificatifs de Jamora", icon: Settings },
  training: { label: "Formations", description: "Catalogue des fonctionnalités Jamora, programmes et sessions de formation. L'équipe d'une mission consulte déjà le catalogue", icon: BookOpen },
  tickets: { label: "Tickets support", description: "Problèmes signalés par les pharmacies : prise en charge, échanges, délais et résolution", icon: Ticket },
  billing: { label: "Revenus", description: "Offres vendues aux pharmacies, abonnements clients, échéances, factures et encaissements", icon: Receipt },
  procurement: { label: "Achats et actifs", description: "Fournisseurs et matériel de Jamora : qui l'a payé, à qui il appartient, qui l'a entre les mains", icon: Package },
  marketing: { label: "Marketing et partenariats", description: "Campagnes, partenaires, origine des pharmacies et coût d'acquisition. Aucun rôle ne les reçoit par défaut", icon: Megaphone },
  governance: { label: "Gouvernance", description: "Décisions, réunions et risques de l'entreprise. Un objet restreint reste visible de ses personnes nommées. Aucun rôle ne les reçoit par défaut", icon: Landmark },
  automation: { label: "Automatisations", description: "Voir, activer, mettre en pause et rejouer les règles que le CRM applique seul. L'arrêt d'urgence du moteur reste réservé à l'administrateur. Aucun rôle ne reçoit ces clés par défaut", icon: Workflow },
  analytics: { label: "Pilotage", description: "Cockpit de direction. La clé ouvre la page ; chaque domaine exige en plus son propre droit « tout voir ». Aucun rôle ne la reçoit par défaut", icon: Gauge },
  architecture: { label: "Cartographie du système", description: "Consulter la carte du CRM : domaines, routes, droits, données, événements, avec les chemins du code. Décrit la structure du système : à réserver à l'équipe technique. Aucun rôle ne la reçoit par défaut", icon: Network },
  jamora_admin: { label: "Jamora App", description: "Voir la plateforme Jamora et ses pharmacies depuis le CRM, en lecture seule. « Ouvrir » est obligatoire ; données métier et journaux sont sensibles. Aucun rôle ne les reçoit par défaut", icon: ShieldCheck },
  knowledge: { label: "Base de connaissance", description: "Procédures internes, documentation support et FAQ : lecture et rédaction. Aucun rôle ne les reçoit par défaut", icon: BookOpen },
  product: { label: "Produit", description: "Retours des équipes, feuille de route, versions livrées et incidents de production. Aucun rôle ne les reçoit par défaut", icon: Lightbulb },
  records: { label: "Documents et contrats", description: "Contrats, documents d'entreprise, signatures, accès document par document", icon: FileText },
  support: { label: "Assistance à distance", description: "File d'attente, console et historique des assistances Jamora", icon: Headset },
  assistant: { label: "Copilote Jamora", description: "Assistant IA : il n'agit jamais au-delà des droits de la personne connectée", icon: Sparkles },
  utilisateurs: { label: "Utilisateurs", description: "Comptes, rôles attribués et activation", icon: UsersIcon },
  outils: { label: "Outils", description: "Outils d'import et d'intégrations", icon: Settings },
  pages: { label: "Pages", description: "Accès aux différentes pages", icon: LayoutGrid },
  administration: { label: "Administration", description: "Paramètres système et journal d'activité", icon: Settings },
};
const CATEGORY_ORDER = ["prospects", "customers", "tasks", "collaborators", "skills", "missions", "equipment", "training", "finance", "billing", "records", "procurement", "marketing", "governance", "analytics", "architecture", "automation", "jamora_admin", "knowledge", "product", "tickets", "support", "assistant", "utilisateurs", "outils", "pages", "administration"];
// Categories where "scope_all" absence means "only my own records", driven
// by a scope_all key of the form "<category>:scope_all" in the catalog.
const SCOPE_LABELS = {
  prospects: { own: "Mes prospects", all: "Tous les prospects" },
  tasks: { own: "Mes tâches", all: "Toutes les tâches" },
  collaborators: { own: "Ma fiche", all: "Tous les collaborateurs" },
};

// Modules on the view_all / manage_all convention: each « all » key is shown as its own
// « mine / all » choice instead of a raw switch. They stay independent: seeing everything never implies acting on everything.
const SCOPE_PAIRS = {
  customers: [
    { key: "customers:view_all", title: "Voir", own: "Ses dossiers clients", all: "Tous les dossiers clients" },
    { key: "customers:manage_all", title: "Suivre", own: "Ses dossiers clients", all: "Tous les dossiers clients" },
  ],
  tickets: [
    { key: "tickets:view_all", title: "Voir", own: "Ses tickets", all: "Tous les tickets" },
    { key: "tickets:manage_all", title: "Traiter", own: "Ses tickets", all: "Tous les tickets" },
  ],
  governance: [
    { key: "governance:view_all", title: "Voir", own: "Ce qui n'est pas restreint, et ce qui me nomme", all: "Tout, y compris les objets restreints" },
  ],
  missions: [
    { key: "missions:view_all", title: "Voir", own: "Ses missions", all: "Toutes les missions" },
    { key: "missions:manage_all", title: "Organiser", own: "Celles créées par soi", all: "Toutes les missions" },
  ],
};
const SCOPE_PAIR_KEYS = new Set(Object.values(SCOPE_PAIRS).flat().map((p) => p.key));

function groupCatalog(catalog) {
  const groups = {};
  for (const perm of catalog) {
    if (perm.key.endsWith(":scope_all") || SCOPE_PAIR_KEYS.has(perm.key)) continue;
    (groups[perm.category] ||= []).push(perm);
  }
  return groups;
}

function permSetsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}

export default function Roles() {
  const { user } = useAuth();
  const [roles, setRoles] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ label: "", description: "" });
  const [creating, setCreating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Global search lands here with { openRole: id } (select that role) or { openCreate: true } (« Nouveau rôle »).
  useEffect(() => {
    const { openRole, openCreate } = location.state || {};
    if (!openRole && !openCreate) return;
    if (openRole) {
      if (!roles) return; // wait for the list
      if (roles.some((r) => r.id === openRole)) setSelectedId(openRole);
    }
    if (openCreate) setCreateOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, roles, location.pathname, navigate]);

  const load = async (keepSelection = true) => {
    try {
      const [{ data: r }, { data: c }, { data: u }] = await Promise.all([
        api.get("/roles"),
        api.get("/permissions/catalog"),
        api.get("/users"),
      ]);
      setRoles(r);
      setCatalog(c);
      setUsers(u);
      if (!keepSelection || !r.some((role) => role.id === selectedId)) {
        setSelectedId(r[0]?.id ?? null);
      }
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => {
    load(false);
    // Loaded once on mount; every later reload is explicit (after a save, a creation or a deletion).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userCountByRole = useMemo(() => {
    const counts = {};
    for (const u of users) counts[u.role] = (counts[u.role] || 0) + 1;
    return counts;
  }, [users]);

  const filteredRoles = useMemo(() => {
    if (!roles) return [];
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => r.label.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [roles, search]);

  if (user?.role !== "admin") return <Navigate to="/pipeline" replace />;

  const selectedRole = roles?.find((r) => r.id === selectedId) || null;

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/roles", { ...createForm, permissions: [] });
      toast.success("Rôle créé");
      setCreateOpen(false);
      setCreateForm({ label: "", description: "" });
      await load(false);
      setSelectedId(data.id);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const deleteRole = async (role, reason) => {
    try {
      await api.delete(`/roles/${role.id}`, { data: { reason } });
      toast.success("Rôle mis à la corbeille");
      await load(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (roles === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-jamora" />
      </div>
    );

  return (
    <div className="space-y-4" data-testid="roles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Rôles & permissions</h1>
          <p className="text-sm text-slate-500">Gérez les accès et les autorisations de votre équipe.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-roles-new">
          <Plus size={16} className="mr-1.5" /> Nouveau rôle
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
        <div className="space-y-2 lg:sticky lg:top-[5.5rem] lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto" data-testid="roles-list">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <Input
              data-testid="input-role-search"
              placeholder="Rechercher un rôle…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="space-y-2">
            {filteredRoles.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                data-testid={`role-card-${r.name}`}
                className={`w-full text-left p-3.5 rounded-xl border transition flex items-start gap-3 ${
                  selectedId === r.id
                    ? "border-jamora bg-jamora/5"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  r.is_system ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"
                }`}>
                  {r.is_system ? <Crown size={16} /> : <Shield size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-900">{r.label}</span>
                    {r.is_system && <Badge variant="outline" className="text-2xs">Système</Badge>}
                  </div>
                  {r.description && (
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.description}</div>
                  )}
                  <div className="text-2xs text-slate-400 mt-1">
                    {userCountByRole[r.name] || 0} utilisateur{(userCountByRole[r.name] || 0) > 1 ? "s" : ""}
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 flex-shrink-0 mt-1.5" />
              </button>
            ))}
          </div>
        </div>

        {selectedRole && (
          <RoleDetail
            key={selectedRole.id}
            role={selectedRole}
            catalog={catalog}
            userCount={userCountByRole[selectedRole.name] || 0}
            onSaved={() => load(true)}
            onDeleted={(reason) => deleteRole(selectedRole, reason)}
          />
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau rôle</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role-label">Nom du rôle</Label>
              <Input
                id="role-label"
                data-testid="input-role-label"
                required
                value={createForm.label}
                onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-description">Description (optionnelle)</Label>
              <Input
                id="role-description"
                data-testid="input-role-description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </div>
            <p className="text-xs text-slate-500">Vous pourrez configurer les permissions juste après la création.</p>
            <Button
              type="submit"
              data-testid="btn-roles-create-submit"
              disabled={creating}
              className="w-full"
            >
              {creating ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Créer le rôle
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoleDetail({ role, catalog, userCount, onSaved, onDeleted }) {
  const readOnly = !!role.is_system;
  const groups = useMemo(() => groupCatalog(catalog), [catalog]);
  const [permissions, setPermissions] = useState(() => new Set(role.permissions || []));
  const [description, setDescription] = useState(role.description || "");
  const [collapsed, setCollapsed] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmObsolete, setConfirmObsolete] = useState(false);
  // Keys this role can no longer keep (removed from the catalogue, or admin-only): they are never sent back, and saving asks
  // for an explicit confirmation of their removal first (without it, nothing is saved).
  const obsolete = useMemo(() => (readOnly ? [] : obsoletePermissionKeys(permissions, catalog)), [readOnly, permissions, catalog]);

  useEffect(() => {
    setPermissions(new Set(role.permissions || []));
    setDescription(role.description || "");
  }, [role]);

  const savedPermissions = useMemo(() => new Set(role.permissions || []), [role]);
  const dirty = !permSetsEqual(permissions, savedPermissions) || description !== (role.description || "");
  useBottomBar(dirty && !readOnly);

  const togglePermission = (key) => {
    if (readOnly) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setScope = (category, all, key = `${category}:scope_all`) => {
    if (readOnly) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      if (all) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const revert = () => {
    setPermissions(new Set(role.permissions || []));
    setDescription(role.description || "");
  };

  const save = () => {
    if (obsolete.length) setConfirmObsolete(true);
    else persist();
  };

  const persist = async () => {
    setConfirmObsolete(false);
    setSaving(true);
    try {
      await api.patch(`/roles/${role.id}`, { permissions: savablePermissionKeys(permissions, catalog), description });
      toast.success("Permissions mises à jour");
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-xl text-slate-900">{role.label}</h3>
          <p className="text-sm text-slate-500">Configurez les accès accordés à ce rôle.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="outline" className="whitespace-nowrap">
            <UsersIcon size={12} className="mr-1.5" /> {userCount} utilisateur{userCount > 1 ? "s" : ""}
          </Badge>
          {!readOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setConfirmDelete(true)}
              data-testid={`btn-delete-role-${role.name}`}
            >
              <Trash2 size={14} className="mr-1.5" /> Supprimer ce rôle
            </Button>
          )}
        </div>
      </div>

      {obsolete.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="role-obsolete-keys">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm text-slate-900">
              Ce rôle contient {obsolete.length} droit{obsolete.length > 1 ? "s" : ""} qui n'existe{obsolete.length > 1 ? "nt" : ""} plus
              ou qu'un rôle ne peut pas détenir. {obsolete.length > 1 ? "Ils seront retirés" : "Il sera retiré"} au prochain enregistrement,
              après votre confirmation.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {obsolete.map((key) => (
                <code key={key} className="text-xs bg-white border border-amber-200 rounded px-1.5 py-0.5 text-slate-700 break-all">{key}</code>
              ))}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="btn-remove-obsolete-keys" className="flex-shrink-0">
            Retirer ces droits
          </Button>
        </div>
      )}

      {CATEGORY_ORDER.filter((cat) => groups[cat]?.length).map((category) => {
        const meta = CATEGORY_META[category];
        const perms = groups[category].filter((p) => !p.admin_only);
        const reserved = groups[category].filter((p) => p.admin_only);
        const enabledCount = perms.filter((p) => permissions.has(p.key)).length;
        const isCollapsed = !!collapsed[category];
        const scopeLabels = SCOPE_LABELS[category];
        const scopeKey = `${category}:scope_all`;
        const hasScope = catalog.some((p) => p.key === scopeKey);
        const Icon = meta.icon;
        return (
          <div key={category} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setCollapsed((p) => ({ ...p, [category]: !p[category] }))}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50"
              data-testid={`toggle-category-${category}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-jamora/10 text-jamora flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <div className="text-left">
                  <div className="font-display font-semibold text-sm text-slate-900">{meta.label}</div>
                  <div className="text-xs text-slate-500">{meta.description}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 whitespace-nowrap">{enabledCount} / {perms.length} autorisations</span>
                {isCollapsed ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronUp size={16} className="text-slate-400" />}
              </div>
            </button>
            {!isCollapsed && (
              <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-start justify-between gap-3 py-1">
                      <span className="min-w-0">
                        <span className="block text-sm text-slate-900">{p.label}</span>
                        {missingPrerequisites(p.key, permissions).length > 0 && (
                          <span className="block text-xs text-amber-700 mt-0.5" data-testid={`perm-prerequisite-${p.key}`}>
                            Nécessite aussi : {missingPrerequisites(p.key, permissions).map((k) => catalog.find((c) => c.key === k)?.label || k).join(", ")}
                          </span>
                        )}
                      </span>
                      <Switch
                        checked={permissions.has(p.key)}
                        onCheckedChange={() => togglePermission(p.key)}
                        disabled={readOnly}
                        data-testid={`switch-perm-${p.key}`}
                      />
                    </label>
                  ))}
                  {reserved.map((p) => (
                    <div key={p.key} className="flex items-start justify-between gap-3 py-1" data-testid={`admin-only-${p.key}`}>
                      <span className="min-w-0">
                        <span className="block text-sm text-slate-500">{p.label}</span>
                        <span className="block text-xs text-slate-400">Réservé à l'administrateur, non délégable</span>
                      </span>
                      <Lock size={14} className="text-slate-400 flex-shrink-0 mt-1" aria-label="Non délégable" />
                    </div>
                  ))}
                </div>
                {(SCOPE_PAIRS[category] || []).filter((pair) => catalog.some((p) => p.key === pair.key)).map((pair) => (
                  <div key={pair.key} className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2" data-testid={`scope-pair-${pair.key}`}>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Portée : {pair.title}</div>
                      {missingPrerequisites(pair.key, permissions).length > 0 ? (
                        <div className="text-xs text-amber-700" data-testid={`perm-prerequisite-${pair.key}`}>
                          Nécessite aussi : {missingPrerequisites(pair.key, permissions).map((k) => catalog.find((c) => c.key === k)?.label || k).join(", ")}
                        </div>
                      ) : <div className="text-xs text-slate-500">{catalog.find((c) => c.key === pair.key)?.label}</div>}
                    </div>
                    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                      {[[false, pair.own, User, "own"], [true, pair.all, UsersIcon, "all"]].map(([all, label, PairIcon, id]) => (
                        <button key={id} type="button" disabled={readOnly} onClick={() => setScope(category, all, pair.key)} data-testid={`scope-${id}-${pair.key}`}
                          aria-pressed={permissions.has(pair.key) === all}
                          className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 ${all ? "border-l border-slate-200" : ""} ${
                            permissions.has(pair.key) === all ? "bg-jamora text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                          <PairIcon size={13} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {hasScope && scopeLabels && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Portée des données</div>
                      <div className="text-xs text-slate-500">Quels {meta.label.toLowerCase()} ce rôle peut-il voir et modifier ?</div>
                    </div>
                    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => setScope(category, false)}
                        data-testid={`scope-own-${category}`}
                        className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 ${
                          !permissions.has(scopeKey) ? "bg-jamora text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <User size={13} /> {scopeLabels.own}
                      </button>
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => setScope(category, true)}
                        data-testid={`scope-all-${category}`}
                        className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 border-l border-slate-200 ${
                          permissions.has(scopeKey) ? "bg-jamora text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <UsersIcon size={13} /> {scopeLabels.all}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="font-display font-semibold text-sm text-slate-900">Informations du rôle</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Nom du rôle</Label>
            <Input value={role.label} disabled data-testid="input-role-name-readonly" />
            <p className="text-2xs text-slate-400">Le nom du rôle ne peut pas être modifié après la création.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Description (optionnelle)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={readOnly}
              data-testid="input-role-description-edit"
            />
          </div>
        </div>
      </div>

      {dirty && !readOnly && (
        <BottomActionBar className="justify-between">
          <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-jamora" /> Des modifications ont été apportées
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={revert} data-testid="btn-revert-role">Annuler</Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              data-testid="btn-save-role"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Enregistrer les modifications
            </Button>
          </div>
        </BottomActionBar>
      )}

      <Dialog open={confirmObsolete} onOpenChange={setConfirmObsolete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Retirer {obsolete.length} droit{obsolete.length > 1 ? "s" : ""} de « {role.label} » ?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              {obsolete.length > 1 ? "Ces droits n'existent plus" : "Ce droit n'existe plus"} ou ne peu{obsolete.length > 1 ? "vent" : "t"} pas
              être attribué{obsolete.length > 1 ? "s" : ""} à un rôle. {obsolete.length > 1 ? "Ils seront retirés" : "Il sera retiré"} de ce rôle :
            </p>
            <div className="flex flex-wrap gap-1.5">
              {obsolete.map((key) => (
                <code key={key} className="text-xs bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700 break-all">{key}</code>
              ))}
            </div>
            <p>Les autres autorisations du rôle sont enregistrées telles que vous les avez réglées.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmObsolete(false)}>Annuler</Button>
            <Button onClick={persist} data-testid="btn-confirm-remove-obsolete">
              Retirer et enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteWithReasonDialog
        target={confirmDelete ? role : null} testId="delete-role" onClose={() => setConfirmDelete(false)}
        title={`Supprimer le rôle « ${role.label} » ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un rôle encore attribué à des utilisateurs ne peut pas être supprimé : réassignez-les d'abord."
        onConfirm={(reason) => { setConfirmDelete(false); onDeleted(reason); }}
      />
    </div>
  );
}
